import { useCallback, useEffect, useRef, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import { Button } from '@/components/ui/Button'
import {
  plan_product_pricings_edit_path,
  plan_product_pricings_export_path,
  plan_product_pricings_index_path,
  plan_product_pricings_remove_product_path,
  plan_product_pricings_submit_path,
} from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import PlanHeader from '../components/PlanHeader'
import PlanSwitcher from '../TopDown/components/PlanSwitcher'
import type { PricingPageProps } from './pricingTypes'
import type { TaskWidget } from '../TopDown/components/PlanTaskWidget'
import PricingChannelTabs from './components/PricingChannelTabs'
import ChannelTaskWidget from '../components/ChannelTaskWidget'
import ChannelRevisionSubmitWidget, { type ChannelRevisionSubmitData } from '../components/ChannelRevisionSubmitWidget'
import { pricingMissing } from './components/pricingMissing'
import PricingNotice from './components/PricingNotice'
import PricingTable from './components/PricingTable'
import { DraftActivityBanner, RevisionBanner } from '../TopDown/components/EditorHeader'
import UploadPricingsModal from './components/UploadPricingsModal'
import AddPricingProductModal from './components/AddPricingProductModal'
import ManagePriceLevelsModal from './components/ManagePriceLevelsModal'
import { useSaveStatus, type CellRef } from './utils/useSaveStatus'
import { usePricingCellSaver } from './utils/usePricingCellSaver'

interface Props extends PricingPageProps {
  // The open "Set Products & Pricings" task for this plan + channel; null
  // unless the viewer is one of its PICs.
  task_widget: TaskWidget | null
  // Someone else's unsubmitted edits in this channel's shared draft (server
  // omits it when the viewer made the last edit).
  draft_activity?: { by: string; at: string } | null
  // Published plan, no pricing task open for the channel: the "Submit for
  // review" popover that opens a revision task (null otherwise).
  pricing_revision_submit?: ChannelRevisionSubmitData | null
  // Whether this channel has an open draft (edits not yet submitted).
  draft_open?: boolean
}

// Edit page: inline prices, CSV upload, add/remove product, price levels. The
// controller only renders this for holders of
// planning.manage_products_and_pricing on the channel (or its market / root),
// and `channels` lists only the ones the viewer may edit; the read-only view
// is ProductPricings.
export default function ProductPricingsEdit({ plan, price_levels, channels, channel, groups, plan_switcher, task_widget, draft_activity, pricing_revision_submit, draft_open = false }: Props) {
  const market = useMarket()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [addProductOpen, setAddProductOpen] = useState(false)
  const [manageLevelsOpen, setManageLevelsOpen] = useState(false)
  const [localGroups, setLocalGroups] = useState(groups)
  const { statusFor, markSaved, markError, errorMessage } = useSaveStatus()

  // `draft_open` is a server prop: the first edit on a channel without a draft
  // opens one, but the page would keep showing "No changes yet" (and no
  // revision banner) until a full reload. After a successful save while the
  // prop says no draft, re-fetch just that prop. Read through a ref so the
  // callback keeps a stable identity for the cell saver's debounce.
  const draftOpenRef = useRef(draft_open)
  useEffect(() => {
    draftOpenRef.current = draft_open
  }, [draft_open])
  const refreshDraftOpen = useCallback(() => {
    if (!draftOpenRef.current) router.reload({ only: ['draft_open'] })
  }, [])
  const onCellsSaved = useCallback(
    (cells: CellRef[]) => {
      markSaved(cells)
      refreshDraftOpen()
    },
    [markSaved, refreshDraftOpen],
  )

  const { saveCell } = usePricingCellSaver({
    marketSlug: market.slug,
    planSlug: plan.slug,
    channelSlug: channel?.slug ?? '',
    currency: plan.currency ?? market.currency,
    setLocalGroups,
    markSaved: onCellsSaved,
    markError,
  })

  useEffect(() => {
    setLocalGroups(groups)
  }, [groups])

  // Removes the product from this channel's plan list only — its stored
  // prices stay, and re-adding the product surfaces them again.
  function removeProduct(productExternalId: string) {
    if (!channel) return
    const name =
      localGroups.flatMap((g) => g.rows).find((r) => r.product_external_id === productExternalId)?.product_name ??
      productExternalId
    if (!window.confirm(`Remove ${name} from ${channel.name}?`)) return
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
    fetch(plan_product_pricings_remove_product_path(market.slug, plan.slug, channel.slug, productExternalId), {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken, Accept: 'application/json' },
    }).then((res) => {
      if (res.ok) router.reload({ only: ['groups', 'draft_open'] })
    })
  }

  const header = (
    <PlanHeader
      name={plan.name}
      orgNodeName={plan.org_node?.name ?? null}
      revenueTarget={plan.revenue_target}
      currency={plan.currency}
      beginDate={plan.begin_date}
      endDate={plan.end_date}
    />
  )

  const submitHref = plan_product_pricings_submit_path(market.slug, plan.slug, channel ? { channel: channel.slug } : {})

  return (
    <AppLayout
      header={header}
      headerActions={
        task_widget && channel ? (
          <ChannelTaskWidget task={task_widget} missing={pricingMissing(localGroups, price_levels.length)} submitHref={submitHref} />
        ) : pricing_revision_submit && channel ? (
          <ChannelRevisionSubmitWidget
            subject="pricings"
            channelName={channel.name}
            draftOpen={draft_open}
            missing={pricingMissing(localGroups, price_levels.length)}
            submitHref={submitHref}
          />
        ) : undefined
      }
    >
      <Head title="Products & Pricings" />
      {channel && (
        <>
          <UploadPricingsModal
            slug={plan.slug}
            channelSlug={channel.slug}
            channelName={channel.name}
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            priceLevels={price_levels}
          />
          <AddPricingProductModal
            slug={plan.slug}
            channelSlug={channel.slug}
            channelName={channel.name}
            planCurrency={plan.currency}
            open={addProductOpen}
            onClose={() => setAddProductOpen(false)}
            onAdded={() => router.reload({ only: ['groups', 'draft_open'] })}
          />
          <ManagePriceLevelsModal
            planSlug={plan.slug}
            channelSlug={channel.slug}
            channelName={channel.name}
            levels={price_levels}
            open={manageLevelsOpen}
            onClose={() => setManageLevelsOpen(false)}
          />
        </>
      )}
      {/* One row, like the Top Down edit pages: plan switcher (no page tabs),
          the channel notice, Upload CSV. A plan switch lands on the show page
          of the newly selected plan. */}
      <div className="mb-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="justify-self-start border rounded-lg px-4 py-2.5"
        >
          <PlanSwitcher data={plan_switcher} redirectPath={plan_product_pricings_index_path(market.slug)} />
        </div>
        <PricingNotice>
          {channel ? (
            <>
              1. Use Manage Price Levels button to add/remove price levels.<br />
              2. Then add product and set pricings for them.<br />
              3. Alternatively, you can upload CSV.<br />
              <strong>After you finish, please submit</strong>.
            </>
          ) : (
            'No sale channels under this plan yet.'
          )}
        </PricingNotice>
        {channel && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={plan_product_pricings_export_path(market.slug, plan.slug, channel.slug)}>Export CSV</a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)}>
              Upload CSV
            </Button>
          </div>
        )}
      </div>
      {draft_open && <RevisionBanner />}
      {draft_activity && <DraftActivityBanner activity={draft_activity} />}
      {errorMessage && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
          {errorMessage}
        </div>
      )}
      {channel && (
        <div className="overflow-hidden rounded-md border bg-card">
          <PricingChannelTabs
            channels={channels}
            activeSlug={channel.slug}
            hrefFor={(slug) => plan_product_pricings_edit_path(market.slug, plan.slug, { channel: slug })}
            trailing={
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto shrink-0 self-center h-7 text-sm text-link"
                onClick={() => setManageLevelsOpen(true)}
              >
                Manage Price Levels
              </Button>
            }
          />
          <PricingTable
            groups={localGroups}
            priceLevels={price_levels}
            canEdit
            emptyMessage={`No products for ${channel.name} yet. Use Add Product or Upload CSV`}
            statusFor={statusFor}
            onCellChange={(productExternalId, level, price) =>
              saveCell({ product_external_id: productExternalId, level, price })
            }
            onRemove={removeProduct}
            onAddProduct={() => setAddProductOpen(true)}
          />
        </div>
      )}
    </AppLayout>
  )
}
